import axios from 'axios';
import { API_URL, STORAGE_KEYS } from '@/utils/constants';
import type { AuthResponse, MeResponse } from '@/types';
import { useAuthStore } from '@/store/auth';

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Platform': 'web',
  },
});

const getAccessToken = () => localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
const getRefreshToken = () => localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

export const setSession = (accessToken: string, refreshToken: string, sessionId: string) => {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
};

export const setTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
};

export const clearTokens = () => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
  localStorage.removeItem(STORAGE_KEYS.OTP_PHONE);
};

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Let the browser set multipart boundary — a bare multipart Content-Type breaks multer.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  return config;
});

let isRefreshing = false;
let pendingRequests: Array<(token: string) => void> = [];

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean };
    if (error.response?.status !== 401 || originalRequest?._retry) {
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearTokens();
      useAuthStore.getState().logout();
      window.location.href = '/auth/phone';
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve) => {
        pendingRequests.push((token: string) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(apiClient(originalRequest));
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const response = await axios.post<{ data: AuthResponse }>(`${API_URL}/auth/refresh`, {
        refreshToken,
      });
      const data = response.data.data;
      useAuthStore.getState().setAuth(data);
      pendingRequests.forEach((cb) => cb(data.tokens.accessToken));
      pendingRequests = [];
      originalRequest.headers.Authorization = `Bearer ${data.tokens.accessToken}`;
      return apiClient(originalRequest);
    } catch {
      clearTokens();
      useAuthStore.getState().logout();
      window.location.href = '/auth/phone';
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }
);
